import { Skeleton } from '@tiger/bs-comps';
import { useSWR } from '@tiger/swr';
import { useMemo } from 'react';
import EmptyData from 'components/EmptyData';
import ErrorBox from 'components/ErrorBox';
import API from 'utils/API';
import { AgreementProps } from '../type';

/**
 * @description 单个协议内容展示组件。
 *  可接受上层透传的id、content，某些场景下可直接使用content，不必再依赖id请求接口获取协议内容。
 */
const AgreementContent: React.FC<Pick<AgreementProps, 'id' | 'linkCode' | 'className' | 'content'>> = ({
    id,
    className,
    linkCode,
    content: contentProp
}) => {
    /**
     * 1. 优先使用content的内容, content即为协议本身的内容
     * 2. 否则如果linkCode存在就是用LinkCode获取协议内容
     * 3. 否则使用id获取协议内容
     */
    const agreementType = useMemo(() => {
        if (contentProp) {
            return 'content';
        }

        if (linkCode) {
            return 'linkCode';
        }

        if (id) {
            return 'id';
        }

        return 'content';
    }, [contentProp, linkCode, id]);
    const { isLoading, error, data, mutate } = useSWR<string>(
        () => {
            if (agreementType === 'content') {
                return null;
            }

            if (agreementType === 'linkCode' && linkCode) {
                return API.SUPPORT.getAgreementContentByLinkCode(linkCode);
            }

            if (agreementType === 'id' && id) {
                return API.SUPPORT.getAgreementContentById(id);
            }

            return null;
        },
        {
            dataChain: res => {
                if (agreementType === 'linkCode') {
                    return res.data?.content;
                }

                if (agreementType === 'id') {
                    return res.data;
                }

                return res;
            },
            // 协议不用变动
            immutable: true
        }
    );
    const content = data || contentProp;

    if (isLoading) {
        return (
            <div className="my-3">
                <Skeleton line={5} />
            </div>
        );
    }

    if (error) {
        return <ErrorBox error={error} onClick={() => mutate()} />;
    }

    if (!content) {
        // 从接口或者prop中都没有获取到协议内容
        return <EmptyData />;
    }

    return <div className={className} dangerouslySetInnerHTML={{ __html: content }} />;
};

export default AgreementContent;
